import { Box, keyframes, Stack, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { InHouseTooltip } from "../../../components/InHouseTooltip/InHouseTooltip";
import { SignedSlider } from "../../../components/UIToolkit/Inputs/SignedSlider";
import { InfoIconWithTooltip } from "../../../components/UIToolkit/molecules/InfoIconWithTooltip";
import { FONT_SIZE, MOTION } from "../../../theme/tokens";
import type { PbjModelUpsertBody, PbjPrediction } from "../../../utils/models";
import { PBJ_ALPHA } from "./colors";
import type { PeriodMode } from "./RecommendationsSidebar";
import { calculateRecData, displaySpendForLabel, MAX_HPPD_PER_RECOMMENDATION } from "./recMath.utils";
import { CUT_SIDE_MIN_SHARE, cutFloorState, OVERRIDE_KEY_BY_WAGE_KEY, SPEND_STEP, wageTooltipKey } from "./recommendationRow.utils";
import type { RecommendationType } from "./recommendations.constants";

// One-shot land-pulse for a slider the Suggest action just repositioned: a ring grows out of the thumb
// and fades (ENTRANCE_MD, ease-out) so the eye catches the move. Drawn on the thumb's own `::after` (MUI's
// invisible 42px hit area — only its border and opacity change, never its size) and animated on
// transform + opacity only, so it stays on the compositor. Applied only while the lever is suggested,
// so it never fires on untouched or user-dragged sliders; reduced motion leaves the ring hidden. The
// color is injected at render time (theme-derived) via a CSS var so the keyframe stays static.
const PULSE_RING = {
	// Scale of the 42px hit area the ring starts and ends at: from just around the thumb to ~2× it.
	FROM_SCALE: 0.3,
	TO_SCALE: 0.6,
	BORDER_PX: 3,
} as const;
// Includes the column's 8px horizontal padding on each side, leaving a 100px rail that is
// comfortable to grab even when the outcome copy is long.
const SLIDER_COLUMN_MIN_WIDTH_PX = 116;
const SLIDER_LAND_PULSE = keyframes`
	from { opacity: 1; transform: translate(-50%, -50%) scale(${PULSE_RING.FROM_SCALE}); }
	to { opacity: 0; transform: translate(-50%, -50%) scale(${PULSE_RING.TO_SCALE}); }
`;

type Props = {
	rec: RecommendationType;
	wage: number;
	// Theme-derived categorical accent for this lever.
	color: string;
	// Live slider spend ($/applicable-PPD, signed).
	currentSpend: number;
	isSuggested: boolean;
	isInert: boolean;
	// Bumped by every Suggest / Reset: the slider is keyed on it so the land pulse replays even when the
	// suggested value didn't change. Never changes on a drag, so the thumb keeps focus mid-drag.
	suggestNonce: number;
	prediction: PbjPrediction;
	census: number;
	periodMode: PeriodMode;
	// The live config draft — read only to tell the wage tooltip override vs. computed.
	draft: PbjModelUpsertBody;
	onChange: (newSpend: number) => void;
};

/**
 * The lever card's control column: the "Spend PPD" / "Savings PPD" readout with its wage tooltip, the
 * two-direction slider (add right of 0, cut left of it, down to the backend's per-lever cut limit) with
 * its end captions. The note naming the cut limit lives on the row, full width (see RecommendationRow).
 */
export const RecommendationSliderControl: FC<Props> = ({
	rec,
	wage,
	color,
	currentSpend,
	isSuggested,
	isInert,
	suggestNonce,
	prediction,
	census,
	periodMode,
	draft,
	onChange,
}) => {
	const { t } = useTranslation();
	const theme = useTheme();
	const pulseRingC = alpha(theme.palette.primary.main, PBJ_ALPHA.BORDER);
	const savingsInk = theme.palette.success.main;
	const isCut = currentSpend < 0;

	// Slider max = the largest HPPD add (`impact = spend / wage`, so `max = wage ×
	// MAX_HPPD_PER_RECOMMENDATION`, the same cap the BE greedy enforces); slider min = the backend's
	// per-lever cut limit converted to spend (0 when the lever can't be cut). Both bounds widen to
	// `currentSpend` so a plan value can never sit outside the rail.
	const { cutFloor } = cutFloorState(rec, prediction, wage, census, currentSpend);
	const sliderMin = Math.min(-cutFloor, currentSpend);
	const sliderMax = Math.max(wage * MAX_HPPD_PER_RECOMMENDATION, currentSpend);

	// Every dollar figure here — the readout, the end captions, the screen-reader value — is the
	// per-CALENDAR-day rate over the window the period toggle measures, so weekday and weekend levers
	// compare directly and match the Action Plan's per-item PPD. The slider's own value stays in raw
	// $/applicable-PPD units; only what's shown is normalized.
	// A cut is shown at its PRICED spend — the whole hours a day it commits (recMath `pricedSpendFor`) —
	// so the readout, the screen-reader value and the deepest-cut caption match the card's "Saves $X" and
	// the Action Plan's "$X PPD saved". An add shows its exact spend, as it always has.
	const pricedAt = (spend: number): number => (spend < 0 ? calculateRecData(rec, spend, wage, prediction, census).pricedSpend : spend);
	const displayDollars = (spend: number): string => displaySpendForLabel(rec, Math.abs(pricedAt(spend)), prediction, periodMode).toFixed(2);
	const ariaValueText = (value: number): string =>
		t(value < 0 ? "pbj_model.recommendations.slider_value_savings_aria" : "pbj_model.recommendations.slider_value_spend_aria", {
			value: displayDollars(value),
		});
	const endLabels = {
		min:
			sliderMin < 0
				? t("pbj_model.recommendations.slider_min_label", { value: displayDollars(sliderMin) })
				: t("pbj_model.recommendations.spend_format", { value: displayDollars(0) }),
		max: t("pbj_model.recommendations.slider_max_label", { value: displayDollars(sliderMax) }),
	};

	return (
		<Box sx={{ px: 1, flex: 1, minWidth: { xs: 0, sm: SLIDER_COLUMN_MIN_WIDTH_PX } }}>
			<Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center", mb: 0 }}>
				<Stack direction="row" sx={{ alignItems: "center" }}>
					<Typography variant="caption" sx={{ color: "text.secondary" }} data-testid={`pbj-model-recommendation-spend-label-${rec.id}`}>
						{t(isCut ? "pbj_model.recommendations.savings_ppd" : "pbj_model.recommendations.spend_ppd")}
					</Typography>
					<InfoIconWithTooltip
						title={t(wageTooltipKey(rec, draft[OVERRIDE_KEY_BY_WAGE_KEY[rec.wageKey]] != null), { value: wage.toFixed(2) })}
						iconSx={{ fontSize: FONT_SIZE.CAPTION, color: "text.secondary", ml: 0.5, cursor: "help" }}
						dataTestId={`pbj-model-recommendation-wage-info-${rec.id}`}
					/>
				</Stack>
				<InHouseTooltip
					arrow
					placement="top"
					title={
						rec.isWeekend
							? t("pbj_model.action_plan.weekend_spend_tooltip", {
									value: Math.abs(pricedAt(currentSpend)).toFixed(2),
									weekendDays: prediction.quarterWeekendDays,
									quarterDays: prediction.quarterDays,
								})
							: ""
					}
				>
					<Typography
						variant="caption"
						sx={{
							fontWeight: "bold",
							color: isCut ? savingsInk : color,
							cursor: rec.isWeekend ? "help" : "default",
							fontVariantNumeric: "tabular-nums",
							transition: `color ${MOTION.DURATION.COLOR_SHIFT}ms ${MOTION.EASE_OUT}`,
							"@media (prefers-reduced-motion: reduce)": { transition: "none" },
						}}
						data-testid={`pbj-model-recommendation-spend-value-${rec.id}`}
					>
						{t("pbj_model.recommendations.spend_format", { value: displayDollars(currentSpend) })}
					</Typography>
				</InHouseTooltip>
			</Stack>
			<SignedSlider
				// Remount on each Suggest / Reset so the land pulse replays. Keyed on the nonce, never on the
				// value or the suggested flag: those change on the first drag, and a remount mid-drag would
				// drop the thumb's focus and the pointer capture.
				key={suggestNonce}
				value={currentSpend}
				min={sliderMin}
				max={sliderMax}
				step={SPEND_STEP}
				origin={0}
				minSideShare={CUT_SIDE_MIN_SHARE}
				fillColor={{ positive: color, negative: savingsInk }}
				muted={isInert}
				onChange={onChange}
				aria-label={t("pbj_model.recommendations.spend_slider_aria", { title: t(rec.titleKey) })}
				getAriaValueText={ariaValueText}
				endLabels={endLabels}
				dataTestId={`pbj-model-recommendation-slider-${rec.id}`}
				sx={{
					py: 0.5,
					"--pbj-pulse": pulseRingC,
					...(isSuggested
						? {
								"& .MuiSlider-thumb::after": {
									border: `${PULSE_RING.BORDER_PX}px solid var(--pbj-pulse)`,
									boxSizing: "border-box",
									opacity: 0,
									animation: `${SLIDER_LAND_PULSE} ${MOTION.DURATION.ENTRANCE_MD}ms ${MOTION.EASE_OUT} both`,
									"@media (prefers-reduced-motion: reduce)": { animation: "none" },
								},
							}
						: {}),
				}}
			/>
		</Box>
	);
};
